import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Row, Col } from 'src/componentes/grid';

import useAuth from 'src/hooks/useAuth';
import { Schema } from 'src/utils/form';
import Edit from 'src/componentes/edit/edit-dialog';
import Input from 'src/componentes/input';
import ItemOrganizacaoLookup from 'src/lookups/item-organizacao';
import { capitalizeFirstLetter } from 'src/utils/string';
import Radio from 'src/componentes/radio';
import EtapasListView from './Etapas';
import TipoMudancaSolicitacaoGestaoMudancasProjetoLookup from 'src/lookups/TipoMudancaSolicitacaoGestaoMudancasProjetoLookup';
import TipoMudancaSolicitacaoGestaoMudancasIndicadorLookup from 'src/lookups/TipoMudancaSolicitacaoGestaoMudancasIndicadorLookup';
import TipoMudancaSolicitacaoGestaoMudancasRiscoLookup from 'src/lookups/TipoMudancaSolicitacaoGestaoMudancasRiscoLookup';
import TipoMudancaSolicitacaoGestaoMudancasDocumentoLookup from 'src/lookups/TipoMudancaSolicitacaoGestaoMudancasDocumentoLookup';
import TipoFluxoAprovacaoLookup from 'src/lookups/tipo-fluxoaprovacao';
import {
  MODULOS,
  TIPO_FLUXO_APROVACAO,
  TIPO_ITEM_FLUXO_APROVACAO,
} from 'src/utils/constants';
import Checkbox from 'src/componentes/checkbox';
import Info from 'src/componentes/info';

const schema = Schema.object({
  idIntegracao: Schema.string().required(),
  nome: Schema.string().required(),
  etapas: Schema.string().required(),
  organizacao: Schema.string(),
});

const EditForm = ({ model, showErrorRequired }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const modelIdIntegracao = model.getModel('idIntegracao');
  const modelNome = model.getModel('nome');
  const modelEtapas = model.getModel('etapas');
  const modelOrganizacao = model.getModel('organizacao');
  const modelTiposMudancasProjeto = model.getModel('tiposMudancasProjeto');
  const modelTiposMudancasIndicador = model.getModel('tiposMudancasIndicador');
  const modelTiposMudancasRisco = model.getModel('tiposMudancasRisco');
  const modelTipoItemFluxoAprovacao = model.getModel('tipoItemFluxoAprovacao');
  const modelTiposMudancasDocumento = model.getModel('tiposMudancasDocumento');
  const modelTipoFluxoAprovacao = model.getModel('tipoFluxoAprovacao');
  const modelReprovarEtapasAposUltrapassarPeriodoDuracao = model.getModel(
    'reprovarEtapasAposUltrapassarPeriodoDuracao'
  );
  const ehFluxoNegociacaoMetas =
    modelTipoFluxoAprovacao &&
    modelTipoFluxoAprovacao.value &&
    modelTipoFluxoAprovacao.value.id == 2;
  const ehStageGate =
    modelTipoFluxoAprovacao &&
    modelTipoFluxoAprovacao.value &&
    modelTipoFluxoAprovacao.value.id == 3;

  const { terms: resources, module } = useAuth();

  const isBelt = module.id == MODULOS.GESTAO_RISCO;

  let disabled = model.getModel('permissions').value
    ? !model.getModel('permissions').value?.allowEdit
    : false;

  const showDeleteIcon = !disabled;

  if (disabled && ehFluxoNegociacaoMetas) {
    disabled = !model.getModel('permissions').value?.allowEditFallback;
  }

  const requestChangeTipoMudancaRisco = (value) => {
    if (!value || value.length === 0) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'msgTipoSolicitacaoObrigatorio' }),
        {
          variant: 'error',
        }
      );
      return;
    }

    if (value && value.length > 1) {
      modelTiposMudancasRisco.requestChange([value[value.length - 1]]);
    } else {
      modelTiposMudancasRisco.requestChange(value);
    }
  };

  const requestChangeTipoMudancaDocumento = (value) => {
    if (!value || value.length === 0) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'msgTipoSolicitacaoObrigatorio' }),
        {
          variant: 'error',
        }
      );
      return;
    }

    if (value && value.length > 1) {
      modelTiposMudancasDocumento.requestChange([value[value.length - 1]]);
    } else {
      modelTiposMudancasDocumento.requestChange(value);
    }
  };

  const ehAvaliacaoRisco = () => {
    return (
      isBelt &&
      modelTiposMudancasRisco?.value &&
      modelTiposMudancasRisco.value.length > 0 &&
      modelTiposMudancasRisco.value[0].id == 1
    );
  };

  const ehGestaoDocumento =
    modelTipoItemFluxoAprovacao &&
    modelTipoItemFluxoAprovacao.value?.id === 4 &&
    modelTipoFluxoAprovacao.value?.id == TIPO_FLUXO_APROVACAO.GESTAO_MUDANCAS;

  const ehGestaoMudancas =
    modelTipoFluxoAprovacao.value?.id == TIPO_FLUXO_APROVACAO.GESTAO_MUDANCAS;

  const tiposSolicitacaoItems = {
    [TIPO_ITEM_FLUXO_APROVACAO.RISCO]: modelTiposMudancasRisco.value,
    [TIPO_ITEM_FLUXO_APROVACAO.DOCUMENTO]: modelTiposMudancasDocumento.value,
  };

  const tipoSolicitacao =
    tiposSolicitacaoItems[modelTipoItemFluxoAprovacao.value?.id]?.[0];

  return (
    <>
      <Row>
        <Col md={3}>
          <TipoFluxoAprovacaoLookup
            model={{
              label: intl.formatMessage({ id: 'tipo' }),
              value: modelTipoFluxoAprovacao.value,
              requestChange: modelTipoFluxoAprovacao.requestChange,
              errors: modelTipoFluxoAprovacao.errors,
            }}
            clearable={false}
            required
            disabled={disabled}
          />
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <Input
            formStyle={{ marginBottom: 0 }}
            disabled={disabled}
            model={{
              label: intl.formatMessage({ id: 'idIntegracao' }),
              value: modelIdIntegracao.value,
              requestChange: modelIdIntegracao.requestChange,
              errors:
                modelIdIntegracao.errors ||
                (showErrorRequired && !modelIdIntegracao.value),
            }}
            required
          />
        </Col>
        <Col md={5}>
          <Input
            formStyle={{ marginBottom: 0 }}
            disabled={disabled}
            model={{
              label: intl.formatMessage({ id: 'nome' }),
              value: modelNome.value,
              requestChange: modelNome.requestChange,
              errors:
                modelNome.errors || (showErrorRequired && !modelNome.value),
            }}
            required
          />
        </Col>
        {
          //se não for negociação de metas
          !ehFluxoNegociacaoMetas && (
            <Col md={4}>
              <ItemOrganizacaoLookup
                formStyle={{ marginBottom: 0 }}
                disabled={disabled}
                model={{
                  label: capitalizeFirstLetter(
                    intl.formatMessage({ id: 'area' }, { area: resources.area })
                  ),
                  value: modelOrganizacao.value,
                  requestChange: modelOrganizacao.requestChange,
                  errors: modelOrganizacao.errors,
                }}
              />
            </Col>
          )
        }
      </Row>

      {
        //se não for negociação de metas
        !ehFluxoNegociacaoMetas && !ehStageGate && (
          <>
            <Row>
              <Col md={12}>
                <Radio
                  disabled={disabled}
                  required={ehGestaoMudancas}
                  model={{
                    label: intl.formatMessage({ id: 'label.tipoItem' }),
                    value: modelTipoItemFluxoAprovacao.value,
                    requestChange: modelTipoItemFluxoAprovacao.requestChange,
                    errors:
                      showErrorRequired && !modelTipoItemFluxoAprovacao.value,
                  }}
                  options={model.getModel('opcoesTipoItemFluxoAprovacao').value}
                  labelField="descricao"
                  name="opcoesTipoItemFluxoAprovacao"
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                {model.getModel('tipoItemFluxoAprovacao') &&
                  model.getModel('tipoItemFluxoAprovacao').value &&
                  model.getModel('tipoItemFluxoAprovacao').value.id ==
                    TIPO_ITEM_FLUXO_APROVACAO.PROJETO && (
                    <TipoMudancaSolicitacaoGestaoMudancasProjetoLookup
                      multi
                      disabled={disabled}
                      model={{
                        label: capitalizeFirstLetter(
                          intl.formatMessage({ id: 'tipoMudanca' })
                        ),
                        value: modelTiposMudancasProjeto.value,
                        requestChange: modelTiposMudancasProjeto.requestChange,
                        errors: modelTiposMudancasProjeto.errors,
                      }}
                    />
                  )}
                {model.getModel('tipoItemFluxoAprovacao') &&
                  model.getModel('tipoItemFluxoAprovacao').value &&
                  model.getModel('tipoItemFluxoAprovacao').value.id ==
                    TIPO_ITEM_FLUXO_APROVACAO.ITEM && (
                    <TipoMudancaSolicitacaoGestaoMudancasIndicadorLookup
                      multi
                      disabled={disabled}
                      model={{
                        label: capitalizeFirstLetter(
                          intl.formatMessage({ id: 'tipoMudanca' })
                        ),
                        value: modelTiposMudancasIndicador.value,
                        requestChange:
                          modelTiposMudancasIndicador.requestChange,
                        errors: modelTiposMudancasIndicador.errors,
                      }}
                    />
                  )}
                {model.getModel('tipoItemFluxoAprovacao') &&
                  model.getModel('tipoItemFluxoAprovacao').value &&
                  model.getModel('tipoItemFluxoAprovacao').value.id ==
                    TIPO_ITEM_FLUXO_APROVACAO.RISCO && (
                    <TipoMudancaSolicitacaoGestaoMudancasRiscoLookup
                      multi
                      disabled={disabled}
                      required
                      model={{
                        label: capitalizeFirstLetter(
                          intl.formatMessage({ id: 'tipoSolicitacao' })
                        ),
                        value: tipoSolicitacao,
                        requestChange: requestChangeTipoMudancaRisco,
                        errors: modelTiposMudancasRisco.errors,
                      }}
                      style={{ marginTop: 25 }}
                    />
                  )}
                {model.getModel('tipoItemFluxoAprovacao') &&
                  model.getModel('tipoItemFluxoAprovacao').value &&
                  model.getModel('tipoItemFluxoAprovacao').value.id ==
                    TIPO_ITEM_FLUXO_APROVACAO.DOCUMENTO && (
                    <TipoMudancaSolicitacaoGestaoMudancasDocumentoLookup
                      multi
                      disabled={disabled}
                      required
                      model={{
                        label: capitalizeFirstLetter(
                          intl.formatMessage({ id: 'tipoSolicitacao' })
                        ),
                        value: tipoSolicitacao,
                        requestChange: requestChangeTipoMudancaDocumento,
                        errors: modelTiposMudancasDocumento.errors,
                      }}
                      style={{ marginTop: 25 }}
                    />
                  )}
              </Col>
              {ehGestaoDocumento && (
                <Col md={6}>
                  <div
                    className="d-flex align-items-end"
                    style={{ marginTop: 25, height: 60 }}
                  >
                    <Checkbox
                      formStyle={{ marginBottom: 5 }}
                      disabled={disabled}
                      model={{
                        label: intl.formatMessage({
                          id: 'labelReprovarEtapasAposUltrapassarPeriodoDuracao',
                        }),
                        value:
                          modelReprovarEtapasAposUltrapassarPeriodoDuracao.value,
                        requestChange:
                          modelReprovarEtapasAposUltrapassarPeriodoDuracao.requestChange,
                        errors:
                          modelReprovarEtapasAposUltrapassarPeriodoDuracao.errors,
                      }}
                    />
                    <div style={{ marginLeft: 5 }}>
                      <Info
                        id="reprovarEtapasAposUltrapassarPeriodoDuracao"
                        placement="top"
                        text={intl.formatMessage({
                          id: 'infoReprovarEtapasAposUltrapassarPeriodoDuracao',
                        })}
                      />
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </>
        )
      }

      <hr></hr>
      <Row>
        <Col md={12}>
          <div>
            <div className="font-weight-bolder text-lg">
              {intl.formatMessage({ id: 'fluxo' })}
            </div>
            <div className="text-muted mt-2 mb-3 text-sm">
              {intl.formatMessage({
                id: 'fluxoAprovacaoGenericoEtapaExplicacao',
              })}
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <EtapasListView
          model={modelEtapas}
          disabled={disabled}
          ehFluxoNegociacaoMetas={ehFluxoNegociacaoMetas}
          showDeleteIcon={showDeleteIcon}
          ehAvaliacaoRisco={ehAvaliacaoRisco()}
          ehGestaoDocumento={ehGestaoDocumento}
          ehGestaoMudancas={ehGestaoMudancas}
          ehStageGate={ehStageGate}
          tipoItem={modelTipoItemFluxoAprovacao.value}
          tipoSolicitacao={tipoSolicitacao}
        />
      </Row>
    </>
  );
};

const EditView = (props) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const showErrorsCustom = (formValue) => {
    let errors = [];
    if (!formValue?.idIntegracao) errors.push('idIntegracao');
    if (!formValue?.nome) errors.push('nome');
    if (
      formValue?.tipoFluxoAprovacao?.id ==
        TIPO_FLUXO_APROVACAO.GESTAO_MUDANCAS &&
      !formValue.tipoItemFluxoAprovacao
    )
      errors.push('tipoItemFluxoAprovacao');

    if (errors.length > 0) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
        }),
        {
          variant: 'error',
        }
      );
    }

    return errors.length > 0;
  };

  const AfterSave = (data) => {
    if (
      data.tipoFluxoAprovacao &&
      data.tipoFluxoAprovacao.id == 2 &&
      !data.permissions.allowEdit &&
      data.permissions.allowEditFallback
    ) {
      enqueueSnackbar(
        intl.formatMessage(
          { id: 'reprocesseFuncaoCiclo' },
          { funcoes: resources.funcoes }
        ),
        {
          variant: 'warning',
          autoHideDuration: 5000,
        }
      );
    }
  };

  return (
    <Edit
      url="/FluxoAprovacaoGenerico"
      title={intl.formatMessage({ id: 'fluxoAprovacao' })}
      schema={schema}
      showErrorsCustom={showErrorsCustom}
      enableShowAllErrors={true}
      formComponent={EditForm}
      width="90%"
      maxWidth="80%"
      maxHeight="80%"
      afterSave={AfterSave}
      intl={intl}
      {...props}
      showExport={false}
    />
  );
};

export default EditView;
